import React, { useState, useEffect, useCallback } from 'react';
import './saude.css';
import { localToday } from '../../utils/dateUtils';
import { Link, useLocation } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import PageHeader from '../../components/PageHeader';
import File from '../../assets/icons/file.svg';
import { toast } from 'react-toastify';
import { useUsers } from '../../context/usersContext';
import { useInternals } from '../../context/internalContext';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import InternalDataSkeleton from '../../components/InternalDataSkeleton';
import Modal from '../../components/modal';
import useSubmitLock from '../../utils/useSubmitLock';
import BinaryFileLink from '../../components/BinaryFileLink';
import { BINARY_RESOURCES, hasBinaryField } from '../../utils/binaryFile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faPen } from '@fortawesome/free-solid-svg-icons';

// Aceita apenas PDF no anexo do status nutricional (único formato de documento aceito pela API)
const NUTRITION_FILE_ACCEPT = '.pdf,application/pdf';
const NUTRITION_FILE_ALLOWED_TYPES = ['application/pdf'];
const NUTRITION_FILE_ALLOWED_EXTENSIONS = ['.pdf'];

const isAllowedNutritionFile = (file) => {
  if (!file) return false;
  const nameLower = (file.name || '').toLowerCase();
  const hasAllowedExtension = NUTRITION_FILE_ALLOWED_EXTENSIONS.some((ext) => nameLower.endsWith(ext));
  const hasAllowedType = NUTRITION_FILE_ALLOWED_TYPES.includes(file.type);
  return hasAllowedType || hasAllowedExtension;
};

// Atualizamos a inicialização do status para incluir o health_professional
const initializeStatusData = (userInfo) => ({
  date: localToday(),
  weight: '',
  height: '',
  imc: '',
  classification: '',
  internal: '',
  health_professional: userInfo?.id || '',
  nutritional_risk: '',
  specific_diet_therapy: '',
  Diet_Specification: '',
  file: null,
});

// Inicialização da evolução permanece inalterada (já incluímos o health_professional)
const initializeEvolutionData = (userInfo) => ({
  date: new Date().toISOString(), // data com hora para evolução
  data: '',
  internal: '',
  health_professional: userInfo?.id || '',
});

const Nutrition = () => {
  const location = useLocation();
  const { internalId } = location.state || {};
  const { userInfo, buildUnitReadParams } = useUsers();
  const {
    listInternals,
    createNutrition,
    listNutrition,
    updateNutrition,
    addNutritionEvolution,
    listNutritionEvolution
  } = useInternals();

  // Estados para busca e dados do interno
  const [internalDetails, setInternalDetails] = useState(null);
  const [currentTab, setCurrentTab] = useState('status');

  // Estados dos formulários separados
  const [statusData, setStatusData] = useState(() => initializeStatusData(userInfo));
  const [evolutionData, setEvolutionData] = useState(() => initializeEvolutionData(userInfo));
  const [evolutions, setEvolutions] = useState([]);
  const [nutritionRecords, setNutritionRecords] = useState([]);
  const [isSelectingInternal, setIsSelectingInternal] = useState(false);
  const [editStatusData, setEditStatusData] = useState(null);
  const {
    isSubmitting: isSavingStatus,
    runWithSubmitLock: runSaveStatus,
  } = useSubmitLock();
  const {
    isSubmitting: isSavingEvolution,
    runWithSubmitLock: runSaveEvolution,
  } = useSubmitLock();
  const {
    isSubmitting: isSavingEditStatus,
    runWithSubmitLock: runSaveEditStatus,
  } = useSubmitLock();

  // Atualiza o campo "health_professional" em evolução sempre que o userInfo mudar
  useEffect(() => {
    setEvolutionData(prev => ({
      ...prev,
      health_professional: userInfo?.id || '',
    }));
  }, [userInfo]);

  // Função para selecionar um interno e carregar seus dados de nutrição e evolução
  const handleInternalSelect = useCallback(async (internal) => {
    if (!internal?.id) {
      toast.error("Erro ao selecionar interno.");
      return;
    }

    try {
      // Chamando a API com o ID correto do interno
      const [nutritionRes, evolutionRes] = await Promise.all([
        listNutrition({ internal: internal.id }),
        listNutritionEvolution({ internal: internal.id }),
      ]);

      setInternalDetails(internal);

      // Atualiza os estados dos formulários com os dados retornados
      setStatusData({
        ...initializeStatusData(userInfo),
        internal: internal.id,
        health_professional: userInfo?.id || '',
      });

      setEvolutionData(prev => ({
        ...prev,
        internal: internal.id,
      }));

      setEvolutions(evolutionRes?.results || []);
      setNutritionRecords(nutritionRes?.results || []);

    } catch (error) {
      console.error('Erro ao carregar dados do interno:', error);
      toast.error('Erro ao carregar dados do interno.');
    }
  }, [listNutrition, listNutritionEvolution, userInfo]);

  // Handlers de alteração dos inputs para cada formulário
  const handleStatusInputChange = (e) => {
    const { name, value } = e.target;
    setStatusData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEvolutionInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setEvolutionData(prev => ({
      ...prev,
      [name]: type === 'file' ? files[0] : value,
    }));
  };

  // Envio dos dados do Status Nutricional
  const handleStatusSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    await runSaveStatus(async () => {
      if (!statusData.internal) {
        toast.error('Nenhum interno selecionado.');
        return;
      }
      if (!statusData.date) {
        toast.error('Informe a data.');
        return;
      }
      if (!statusData.weight) {
        toast.error('Informe o peso.');
        return;
      }
      if (!statusData.height) {
        toast.error('Informe a altura.');
        return;
      }
      if (!statusData.imc) {
        toast.error('Informe peso e altura para calcular o IMC.');
        return;
      }
      if (!statusData.classification) {
        toast.error('Selecione a classificação.');
        return;
      }
      if (!statusData.nutritional_risk) {
        toast.error('Selecione o risco nutricional.');
        return;
      }
      if (!statusData.specific_diet_therapy) {
        toast.error('Selecione a dieta específica.');
        return;
      }

      try {
        let payload;
        const hasNewFile = statusData.file && typeof statusData.file === 'object' && 'name' in statusData.file;

        if (hasNewFile) {
          payload = new FormData();
          Object.entries(statusData).forEach(([key, value]) => {
            if (key === 'file' && value) {
              payload.append(key, value);
            } else if (value !== undefined && value !== '' && key !== 'existing_file') {
              payload.append(key, value);
            }
          });
        } else {
          // Se não houver novo arquivo, removemos `file` antes de enviar
          const { file, ...dataWithoutFile } = statusData;
          payload = dataWithoutFile;
        }

        const headers = hasNewFile
          ? { 'Content-Type': 'multipart/form-data' }
          : { 'Content-Type': 'application/json' };

        await createNutrition(payload, { headers });
        // Após criar, buscar todos os registros e atualizar a timeline
        const nutritionListRes = await listNutrition({ internal: statusData.internal });
        setNutritionRecords(nutritionListRes?.results || []);
        setStatusData(initializeStatusData(userInfo));

      } catch (error) {
        console.error('Erro ao salvar dados de nutrição:', error);
      }
    });
  };

  // Envio dos dados da Evolução Nutricional
  const handleEvolutionSubmit = async (e) => {
    e.preventDefault();
    await runSaveEvolution(async () => {
      if (!evolutionData.internal) {
        toast.error('Nenhum interno selecionado.');
        return;
      }

      try {
        let payload = { ...evolutionData };

        // Se houver arquivo, cria um FormData; caso contrário, envia JSON
        if (evolutionData.file) {
          payload = new FormData();
          Object.entries(evolutionData).forEach(([key, value]) => {
            if (key === 'file' && value) {
              payload.append(key, value);
            } else if (value !== undefined && value !== '') {
              payload.append(key, value);
            }
          });
        }

        await addNutritionEvolution(payload);

        // 🔄 Atualiza a lista de evoluções após o envio da nova
        const evolutionResponse = await listNutritionEvolution({ internal: evolutionData.internal });

        setEvolutions(evolutionResponse?.results || []); // Atualiza o estado com os novos dados

        // 🔄 Reseta os campos após o envio
        setEvolutionData((prev) => ({
          ...initializeEvolutionData(userInfo),
          internal: prev.internal,
        }));

      } catch (error) {
        console.error('Erro ao adicionar evolução nutricional:', error);
      }
    });
  };


  // useEffect para buscar interno a partir do internalId passado na rota
  useEffect(() => {
    const fetchInternalById = async () => {
      try {
        const params = buildUnitReadParams({ id: internalId });
        const internalsResponse = await listInternals(params);
        const internalsData = internalsResponse?.results || [];

        if (internalsData.length === 1) {
          handleInternalSelect(internalsData[0]); // Carrega os dados completos do interno
        }
      } catch (error) {
        console.error('Erro ao carregar dados do interno:', error);
      }
    };

    if (internalId) {
      fetchInternalById();
    }
  }, [internalId, listInternals, buildUnitReadParams, handleInternalSelect]);

  // Calcula automaticamente o IMC sempre que peso ou altura mudarem
  useEffect(() => {
    const peso = parseFloat(statusData.weight);
    const altura = parseFloat(statusData.height);

    if (peso && altura) {
      const alturaEmMetros = altura / 100; // converte de cm para m
      const imc = (peso / (alturaEmMetros * alturaEmMetros)).toFixed(1);
      setStatusData(prev => ({ ...prev, imc }));
    }
  }, [statusData.weight, statusData.height]);

  // Calcula automaticamente o IMC do registro em edição
  useEffect(() => {
    const peso = parseFloat(editStatusData?.weight);
    const altura = parseFloat(editStatusData?.height);

    if (peso && altura) {
      const alturaEmMetros = altura / 100;
      const imc = (peso / (alturaEmMetros * alturaEmMetros)).toFixed(1);
      setEditStatusData(prev => (prev ? { ...prev, imc } : prev));
    }
  }, [editStatusData?.weight, editStatusData?.height]);

  // Abre o modal de edição com os dados do registro selecionado
  const handleEditStatusOpen = (rec) => {
    setEditStatusData({
      id: rec.id,
      weight: rec.weight ?? '',
      height: rec.height ?? '',
      imc: rec.imc ?? '',
      classification: rec.classification || '',
      nutritional_risk: rec.nutritional_risk || '',
      specific_diet_therapy: rec.specific_diet_therapy || '',
      diet_specification: rec.diet_specification || '',
      file: null,
      has_existing_file: hasBinaryField(rec, 'file'),
    });
  };

  const handleEditStatusInputChange = (e) => {
    const { name, value } = e.target;
    setEditStatusData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditStatusSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    await runSaveEditStatus(async () => {
      if (!editStatusData?.id) return;

      try {
        const { id, has_existing_file, file, ...rest } = editStatusData;
        let payload;

        if (file) {
          payload = new FormData();
          Object.entries(rest).forEach(([key, value]) => {
            if (value !== undefined && value !== '') {
              payload.append(key, value);
            }
          });
          payload.append('file', file);
        } else {
          payload = rest;
        }

        await updateNutrition(id, payload);
        const nutritionListRes = await listNutrition({ internal: statusData.internal || internalDetails?.id });
        setNutritionRecords(nutritionListRes?.results || []);
        setEditStatusData(null);
      } catch (error) {
        console.error('Erro ao atualizar dados de nutrição:', error);
      }
    });
  };

  // Renderiza o formulário de status (sem botão de envio – este fica fora)
  const renderStatusTab = () => (
    <>
      <form className="nutrition-form">
        <div className="form-group">
          <label>Data <span className="required-indicator">*</span></label>
          <input
            type="date"
            name="date"
            value={statusData.date ? statusData.date.split('T')[0] : ''}
            onChange={handleStatusInputChange}
          />
        </div>
        <div className="form-group">
          <label>Peso <span className="required-indicator">*</span></label>
          <input
            type="number"
            step="0.1"
            name="weight"
            value={statusData.weight}
            onChange={handleStatusInputChange}
          />
        </div>
        <div className="form-group">
          <label>Altura <span className="required-indicator">*</span></label>
          <input
            type="number"
            step="0.1"
            name="height"
            value={statusData.height}
            onChange={handleStatusInputChange}
          />
        </div>
        <div className="form-group">
          <label>IMC <span className="required-indicator">*</span></label>
          <input
            type="number"
            step="0.1"
            name="imc"
            value={statusData.imc}
            onChange={handleStatusInputChange}
            readOnly
          />
        </div>
        <div style={{ display: 'flex', gap: '15px', width: '100%' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label>Classificação <span className="required-indicator">*</span></label>
            <select
              name="classification"
              value={statusData.classification}
              onChange={handleStatusInputChange}
            >
              <option value="">Selecione</option>
              <option value="Abaixo do peso">Abaixo do peso</option>
              <option value="Peso normal">Peso normal</option>
              <option value="Sobrepeso">Sobrepeso</option>
              <option value="Obesidade">Obesidade</option>
            </select>
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label>Riscos Nutricionais <span className="required-indicator">*</span></label>
            <select
              name="nutritional_risk"
              value={statusData.nutritional_risk}
              onChange={handleStatusInputChange}
            >
              <option value="">Selecione</option>
              <option value="Terciário">Terciário</option>
              <option value="Secundário">Secundário</option>
              <option value="Primário">Primário</option>
              <option value="Nenhum">Nenhum</option>
            </select>
          </div>

          <div className="form-group" style={{ flex: 1 }}>
            <label>Dieta Específica <span className="required-indicator">*</span></label>
            <select
              name="specific_diet_therapy"
              value={statusData.specific_diet_therapy}
              onChange={handleStatusInputChange}
            >
              <option value="">Selecione</option>
              <option value="Terciário">Terciário</option>
              <option value="Secundário">Secundário</option>
              <option value="Primário">Primário</option>
              <option value="Nenhum">Nenhum</option>
            </select>
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <label>Dieta personalizada</label>
          <textarea
            rows="3"
            name="Diet_Specification"
            value={statusData.Diet_Specification}
            onChange={handleStatusInputChange}
          />
        </div>

        <div className="form-group">
          <label>Arquivo</label>
          <div className="images-grid">
            {/* Mostra o arquivo existente apenas se não tiver um novo selecionado */}
            {statusData.file && (
              <Link className='arquivo-incluso' to={statusData.file} target='_blank'>
                <div style={{ width: 60, height: 60 }} className="image-thumbnail">
                  <img src={File} alt="Arquivo" />
                </div>
              </Link>
            )}

            <div className='form-cadastro-interno'>
              <div style={{ width: 200, height: 100 }} className='image-upload-container'>
                <input
                  type='file'
                  name="file"
                  accept={NUTRITION_FILE_ACCEPT}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (!isAllowedNutritionFile(file)) {
                      toast.error('Envie apenas arquivos em PDF.');
                      e.target.value = '';
                      return;
                    }
                    setStatusData((prev) => ({ ...prev, file }));
                  }}
                  className="image-input"
                />
                <img src={File} alt="Upload icon" className="upload-icon" />
              </div>
            </div>
          </div>

          <div>
              <button type="button" onClick={handleStatusSubmit} className="social-submit-button" disabled={isSavingStatus}>
                {isSavingStatus ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
        </div>
      </form>
      <div className="timeline">
        <h4>Histórico Nutricional</h4>
        <ul className="timeline-list">
          {nutritionRecords.length > 0 ? (
            nutritionRecords.slice().reverse().map((rec, i) => (
              <li key={rec.id ?? i} className="timeline-item">
                <div className="timeline-date">
                  <FontAwesomeIcon icon={faClock} />{' '}
                  {rec.created_at ? new Date(rec.created_at).toLocaleString('pt-BR') : ''}
                  <button
                    type="button"
                    className="med-edit-btn"
                    onClick={() => handleEditStatusOpen(rec)}
                    title="Editar"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                </div>
                <div className="timeline-content">
                  {rec.admission_summary && (
                    <p>
                      <strong>Coletado na admissão:</strong>{' '}
                      {rec.admission_summary.unit}
                      {rec.admission_summary.created_at &&
                        ` (${new Date(rec.admission_summary.created_at).toLocaleDateString('pt-BR')})`}
                    </p>
                  )}
                  <p><strong>Peso:</strong> {rec.weight} kg</p>
                  <p><strong>Altura:</strong> {rec.height} m</p>
                  <p><strong>IMC:</strong> {rec.imc}</p>
                  <p><strong>Classificação:</strong> {rec.classification}</p>
                  <p><strong>Risco Nutricional:</strong> {rec.nutritional_risk}</p>
                  <p><strong>Terapia Dietética Específica:</strong> {rec.specific_diet_therapy || '-'}</p>
                  <p><strong>Dieta:</strong> {rec.diet_specification}</p>
                  {hasBinaryField(rec, 'file') && (
                    <p><strong>Documento:</strong> <BinaryFileLink resource={BINARY_RESOURCES.nutritionalStatus} id={rec.id} field="file">Ver Documento</BinaryFileLink></p>
                  )}
                </div>
              </li>
            ))
          ) : (
            <p>Nenhum registro nutricional.</p>
          )}
        </ul>
      </div>
    </>
  );

  // Renderiza o formulário de evolução (com seu botão de envio interno)
  const renderEvolutionTab = () => (
    <>
      <form onSubmit={handleEvolutionSubmit} className="nutrition-form">
        <div className="form-group">
          <label>Data</label>
          <input
            type="datetime-local"
            name="date"
            value={evolutionData.date ? evolutionData.date.slice(0, 16) : ''}
            onChange={handleEvolutionInputChange}
          />
        </div>
        <button type="submit" className="social-submit-button" disabled={isSavingEvolution}>
          {isSavingEvolution ? 'Salvando...' : 'Adicionar Evolução Nutricional'}
        </button>
        <div style={{ width: "100%" }}>
          <label>Detalhes</label>
          <textarea
            name="data"
            rows="3"
            value={evolutionData.data}
            onChange={handleEvolutionInputChange}
          />
        </div>
      </form>

      {/* Timeline de evoluções já registradas */}
      <div className="timeline">
        <h4>Evoluções Nutricionais</h4>
        <ul className="timeline-list">
          {evolutions && evolutions.length > 0 ? (
            evolutions.slice().reverse().map((evo, index) => (
              <li key={index} className="timeline-item">
                <div className="timeline-date">
                  {new Date(evo.date).toLocaleString()}
                </div>
                <div className="timeline-content">
                  <p>
                    <strong>Detalhes:</strong> {evo.data || 'Sem detalhes'}
                  </p>
                </div>
              </li>
            ))
          ) : (
            <p>Nenhuma evolução registrada.</p>
          )}
        </ul>
      </div>
    </>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container social-container admission-page">
        <Titulo
          children={
            <SearchInternal
              onSelect={handleInternalSelect}
              onSelectingChange={setIsSelectingInternal}
            />
          }
        />

        <PageHeader
          title="Nutrição Interna"
          subtitle="Avaliação e evolução nutricional."
          internalDetails={internalDetails}
        />

        {isSelectingInternal ? (
          <InternalDataSkeleton />
        ) : (
          <>
        {/* Abas para Status e Evolução Nutricional */}
        <div className="social-tabs">
          <button
            className={`social-tab-button ${currentTab === 'status' ? 'active' : ''}`}
            onClick={() => setCurrentTab('status')}
          >
            Status Nutricional
          </button>
          <button
            className={`social-tab-button ${currentTab === 'evolucao' ? 'active' : ''}`}
            onClick={() => setCurrentTab('evolucao')}
          >
            Evolução Nutricional
          </button>
        </div>

        <div className="social-tab-content">
          {currentTab === 'status' ? renderStatusTab() : renderEvolutionTab()}
        </div>
          </>
        )}
      </div>

      {editStatusData && (
        <Modal
          title="Editar Status Nutricional"
          isOpen={!!editStatusData}
          closeModal={() => setEditStatusData(null)}
        >
          <form onSubmit={handleEditStatusSubmit} className="unit-modal-form unit-modal-grid">
            <div className="unit-modal-field">
              <label>Peso</label>
              <input
                type="number"
                step="0.1"
                name="weight"
                value={editStatusData.weight}
                onChange={handleEditStatusInputChange}
              />
            </div>
            <div className="unit-modal-field">
              <label>Altura</label>
              <input
                type="number"
                step="0.1"
                name="height"
                value={editStatusData.height}
                onChange={handleEditStatusInputChange}
              />
            </div>
            <div className="unit-modal-field">
              <label>IMC</label>
              <input
                type="number"
                step="0.1"
                name="imc"
                value={editStatusData.imc}
                readOnly
              />
            </div>
            <div className="unit-modal-field">
              <label>Classificação</label>
              <select
                name="classification"
                value={editStatusData.classification}
                onChange={handleEditStatusInputChange}
              >
                <option value="">Selecione</option>
                <option value="Abaixo do peso">Abaixo do peso</option>
                <option value="Peso normal">Peso normal</option>
                <option value="Sobrepeso">Sobrepeso</option>
                <option value="Obesidade">Obesidade</option>
              </select>
            </div>
            <div className="unit-modal-field">
              <label>Riscos Nutricionais</label>
              <select
                name="nutritional_risk"
                value={editStatusData.nutritional_risk}
                onChange={handleEditStatusInputChange}
              >
                <option value="">Selecione</option>
                <option value="Terciário">Terciário</option>
                <option value="Secundário">Secundário</option>
                <option value="Primário">Primário</option>
                <option value="Nenhum">Nenhum</option>
              </select>
            </div>
            <div className="unit-modal-field">
              <label>Dieta Específica</label>
              <select
                name="specific_diet_therapy"
                value={editStatusData.specific_diet_therapy}
                onChange={handleEditStatusInputChange}
              >
                <option value="">Selecione</option>
                <option value="Terciário">Terciário</option>
                <option value="Secundário">Secundário</option>
                <option value="Primário">Primário</option>
                <option value="Nenhum">Nenhum</option>
              </select>
            </div>
            <div className="unit-modal-field span-2">
              <label>Dieta personalizada</label>
              <textarea
                rows="3"
                name="diet_specification"
                value={editStatusData.diet_specification}
                onChange={handleEditStatusInputChange}
              />
            </div>
            <div className="unit-modal-field span-2">
              <label>Arquivo</label>
              {editStatusData.has_existing_file && !editStatusData.file && (
                <p>
                  <BinaryFileLink resource={BINARY_RESOURCES.nutritionalStatus} id={editStatusData.id} field="file">
                    Ver arquivo atual
                  </BinaryFileLink>
                </p>
              )}
              <input
                type="file"
                accept={NUTRITION_FILE_ACCEPT}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (!isAllowedNutritionFile(file)) {
                    toast.error('Envie apenas arquivos PDF ou Word (doc/docx).');
                    e.target.value = '';
                    return;
                  }
                  setEditStatusData((prev) => ({ ...prev, file }));
                }}
              />
            </div>
            <div className="unit-modal-actions">
              <button type="submit" className="btnConfirmar" disabled={isSavingEditStatus}>
                {isSavingEditStatus ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Nutrition;
